import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Email,
  Phone,
  Business,
  Work,
  Search,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { hasNavWrite } from "../utils/navPermissions";
import TableLoadingState from "../components/ui/TableLoadingState";
import ActionMenu from "../components/ui/ActionMenu";
import Avatar from "../components/ui/Avatar";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button as MuiButton,
} from "@mui/material";

/** Same labels as Role Management (User.role in API). */
function formatAppRole(role) {
  if (role == null || role === "") return "—";
  const r = String(role).toUpperCase();
  const labels = {
    EMPLOYEE: "Employee",
    HR: "HR",
    MANAGER: "Manager",
    ADMIN: "Admin",
  };
  return labels[r] || `${r.charAt(0)}${r.slice(1).toLowerCase()}`;
}

function appRoleBadgeClass(role) {
  const r = String(role || "").toUpperCase();
  if (r === "ADMIN") return "bg-purple-100 text-purple-700";
  if (r === "MANAGER") return "bg-blue-100 text-blue-700";
  if (r === "HR") return "bg-amber-100 text-amber-700";
  return "bg-green-100 text-green-700";
}

function getMicrosoftRole(userObj) {
  if (!userObj) return "";
  const candidates = [
    userObj.microsoftRole,
    userObj.microsoft_role,
    userObj.azureRole,
    userObj.azure_role,
    userObj.jobTitle,
    userObj.job_title,
    userObj.outlookJobTitle,
    userObj.outlook_job_title,
    userObj.microsoftProfile?.jobTitle,
    userObj.microsoftProfile?.role,
  ];
  const first = candidates.find((v) => typeof v === "string" && v.trim() !== "");
  return first ? first.trim() : "";
}

export default function Employees() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [myTeam, setMyTeam] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");

  const userRole = user?.role?.toUpperCase() || user?.role;
  const isManager = userRole === "MANAGER" || user?.role === "manager";
  const canManageEmployees =
    (user?.role || "").toUpperCase() === "ADMIN" &&
    hasNavWrite(user, "/employees");

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (!user || !isManager) return;
    api
      .get("/manager/dashboard")
      .then((res) => {
        const data = res.data?.data ?? res.data;
        const list = data?.managedEmployees ?? [];
        setMyTeam(Array.isArray(list) ? list : []);
      })
      .catch(() => setMyTeam([]));
  }, [user, isManager]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get("/employees");
      const payload = response.data;
      const employeeData = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      setEmployees(employeeData);
    } catch (err) {
      console.error("Failed to fetch employees", err);
      toast.error("Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (employee) => {
    setEmployeeToDelete(employee);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!employeeToDelete) return;

    try {
      const empId = employeeToDelete.id || employeeToDelete._id;
      if (!empId) {
        toast.error("Invalid employee ID");
        setDeleteDialogOpen(false);
        return;
      }
      await api.delete(`/employees/${empId}`);
      toast.success("Employee deleted successfully");
      setDeleteDialogOpen(false);
      setEmployeeToDelete(null);
      fetchEmployees();
    } catch (err) {
      console.error("Failed to delete employee", err);
      toast.error(err.response?.data?.message || "Failed to delete employee");
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEmployeeToDelete(null);
  };

  const filteredEmployees = useMemo(() => {
    const raw = employeeSearch.trim().toLowerCase();
    if (!raw) return employees;
    const tokens = raw.split(/\s+/).filter(Boolean);
    return employees.filter((emp) => {
      const blob = [
        emp.user_id?.name,
        emp.user_id?.email,
        emp.phone,
        emp.user_id?.phone,
        emp.department_id?.name,
        emp.user_id?.department_id?.name,
        emp.position,
        emp.designation,
        getMicrosoftRole(emp.user_id),
        formatAppRole(emp.user_id?.role),
        String(emp.user_id?.role || ""),
        emp.id,
        emp._id,
        emp.staffUserId,
      ]
        .filter((v) => v != null && v !== "")
        .join(" ")
        .toLowerCase();
      return tokens.every((t) => blob.includes(t));
    });
  }, [employees, employeeSearch]);

  const formatDate = (value) => {
    if (!value) return "--";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "--";
    }
    return parsed.toLocaleDateString();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const responsiveCardGrid =
    "grid gap-6 [grid-template-columns:repeat(auto-fill,minmax(min(100%,17rem),1fr))]";
  const myTeamGrid =
    "grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(min(100%,17rem),1fr))]";

  return (
    <div className="max-w-7xl mx-auto w-full min-w-0">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8 flex flex-col gap-4"
      >
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-4">
          <div>
            <h1 className="text-4xl font-bold text-primary mb-2">
              Employee Management
            </h1>
            <p className="text-gray-600">Manage your organization's employees</p>
          </div>
          <div className="w-full lg:max-w-md flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100">
            <Search className="text-gray-400 w-5 h-5 flex-shrink-0" aria-hidden />
            <input
              type="search"
              className="bg-transparent outline-none text-sm text-gray-700 w-full placeholder:text-gray-400"
              placeholder="Search by name, email, phone, department, title…"
              value={employeeSearch}
              onChange={(e) => setEmployeeSearch(e.target.value)}
              aria-label="Search employees"
            />
          </div>
        </div>
      </motion.div>

      {/* My team (managers only) */}
      {isManager && myTeam.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-primary mb-4">My team</h2>
          <div className="card p-4">
            <p className="text-sm text-gray-600 mb-4">Employees under you</p>
            <div className={myTeamGrid}>
              {myTeam.map((member) => (
                <div
                  key={member.id || member._id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-semibold">
                      {(member.name || "?").charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{member.name || "—"}</p>
                    <p className="text-sm text-gray-500 truncate">{member.email || "—"}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Employees Grid */}
      {loading ? (
        <div className={responsiveCardGrid}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="w-16 h-16 skeleton rounded-full"></div>
                <div className="w-8 h-8 skeleton rounded-xl"></div>
              </div>
              <div className="h-6 skeleton rounded mb-2 w-3/4"></div>
              <div className="h-4 skeleton rounded w-1/2 mb-4"></div>
              <div className="space-y-2">
                <div className="h-4 skeleton rounded w-full"></div>
                <div className="h-4 skeleton rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      ) : employees.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500 text-lg">No employees found</p>
        </div>
      ) : filteredEmployees.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500 text-lg">No employees match your search</p>
          <button
            type="button"
            onClick={() => setEmployeeSearch("")}
            className="mt-3 text-sm font-semibold text-primary hover:underline"
          >
            Clear search
          </button>
        </div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className={responsiveCardGrid}
        >
          {filteredEmployees.map((employee, index) => {
            const empId = employee.id || employee._id;
            const rowKey = empId || employee.staffUserId || `row-${index}`;
            const hasHrDetailsRecord = !!empId;
            const appRole = formatAppRole(employee.user_id?.role);
            const appRoleClass = appRoleBadgeClass(employee.user_id?.role);
            const msRole = getMicrosoftRole(employee.user_id);
            const jobTitle = (employee.position || employee.designation || "").trim();
            const showJobTitle =
              hasHrDetailsRecord &&
              jobTitle &&
              jobTitle.toLowerCase() !== appRole.toLowerCase();

            return (
            <motion.div
              key={rowKey}
              variants={cardVariants}
              className="card group cursor-pointer min-w-0"
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
            >
              <div className="flex items-start justify-between gap-2 mb-4 min-w-0">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                  <Avatar
                    src={employee.user_id?.profile_image}
                    name={employee.user_id?.name || "E"}
                    size="md"
                    className="rounded-2xl flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-primary mb-1 break-words">
                      {employee.user_id?.name || "N/A"}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${appRoleClass}`}>
                        {appRole}
                      </span>
                      {msRole && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-sky-100 text-sky-700">
                          MS: {msRole}
                        </span>
                      )}
                      {showJobTitle && (
                        <span className="text-xs text-gray-500 font-medium">
                          · {jobTitle}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {canManageEmployees && (
                  <div className="flex-shrink-0">
                    <ActionMenu
                      onEdit={(item) => {
                        const eid = item.id || item._id;
                        if (!eid) {
                          navigate("/employees/add");
                          return;
                        }
                        navigate(`/employees/edit/${eid}`);
                      }}
                      onDelete={hasHrDetailsRecord ? handleDeleteClick : undefined}
                      item={employee}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-3 pt-4 border-t border-gray-100 min-w-0">
                <div className="flex items-start gap-3 text-sm min-w-0">
                  <Email className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 break-all min-w-0">
                    {employee.user_id?.email || "N/A"}
                  </span>
                </div>
                <div className="flex items-start gap-3 text-sm min-w-0">
                  <Phone className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 break-all min-w-0">
                    {employee.phone || employee.user_id?.phone || "N/A"}
                  </span>
                </div>
                <div className="flex items-start gap-3 text-sm min-w-0">
                  <Business className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 break-words min-w-0">
                    {employee.department_id?.name || 
                     (employee.user_id?.department_id?.name ? employee.user_id.department_id.name : 
                      (employee.user_id?.department_id ? "Loading..." : "N/A"))}
                  </span>
                </div>
                {employee.leaving_date && (
                  <div className="flex items-start gap-3 text-sm min-w-0">
                    <Work className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 break-words min-w-0">
                      Left: {formatDate(employee.leaving_date)}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete employee "
            {employeeToDelete?.user_id?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleDeleteCancel}>Cancel</MuiButton>
          <MuiButton
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
